import { DEFAULT_LIMIT, gqlHelper, GraphQLClientType, MAX_PAGES } from "./gqlHelper";
import { GetClaimsByCedeSafe, GetCreatedCedeSafe, GetRecentClaims } from "./types";
import { Address, Hex } from "viem";

// Re-export types
export type { GetClaimsByCedeSafe, GetCreatedCedeSafe, GetRecentClaims };

// Re-export gqlHelper utilities for custom indexers
export { gqlHelper, DEFAULT_LIMIT, MAX_PAGES };
export type { GraphQLClientType };

// gql template tag type - apps must provide graphql-request implementation
export type GqlTagType = (chunks: TemplateStringsArray, ...variables: unknown[]) => string;

export type CedeSafeCreatedEvent = {
  id: string;
  cedeSafeId: string;
  cedeSafeAddress: Address;
  timestamp: string;
};

export type RecentClaimEvent = {
  id: string;
  chainId: number;
  cedeSafe: string;
  epochId: string;
  user: string;
  amount: string;
  timestamp: string;
  txHash: Hex;
  user_cedeSafe_epochId: string;
};

export async function getClaimsByCedeSafe(
  cedeSafe: Address,
  chainId: number,
  GQL_URL: string,
  gql: GqlTagType,
  GraphQLClientClass: new (url: string) => GraphQLClientType,
  /** Unix seconds — only claims with `timestamp > fromTimestamp` are returned. Defaults to 0 (all). */
  fromTimestamp = 0,
  limit = DEFAULT_LIMIT,
  maxPages = MAX_PAGES,
): Promise<GetClaimsByCedeSafe> {
  // Cede Safe addresses can collide across chains (CREATE2), so scope by chainId.
  const CLAIMS_BY_CEDESAFE_QUERY = gql`
    query claimsByCedeSafe($cedeSafe: String!, $chainId: Int!, $fromTimestamp: numeric!, $limit: Int!, $after: Int) {
      CedeRewardsSafeModule_RewardsClaimed(
        where: { cedeSafe: { _eq: $cedeSafe }, chainId: { _eq: $chainId }, timestamp: { _gt: $fromTimestamp } }
        limit: $limit
        offset: $after
      ) {
        id
        chainId
        cedeSafe
        epochId
        user
        amount
        timestamp
        txHash
      }
    }
  `;
  type Raw = RecentClaimEvent;
  type Resp = { CedeRewardsSafeModule_RewardsClaimed: Raw[] };
  const allClaims = await gqlHelper<
    Raw,
    { cedeSafe: string; chainId: number; fromTimestamp: number; limit: number; after: number },
    Resp
  >(
    GQL_URL,
    CLAIMS_BY_CEDESAFE_QUERY,
    // The indexer stores `cedeSafe` lowercase (same as getEpochCreatedEvents /
    // getClaimsByUser) — lowercase here so the exact `_eq` match doesn't miss.
    (offset, limit) => ({ cedeSafe: cedeSafe.toLowerCase(), chainId, fromTimestamp, limit, after: offset }),
    (res) => res.CedeRewardsSafeModule_RewardsClaimed,
    GraphQLClientClass,
    limit,
    maxPages,
  );
  return allClaims.map((el) => ({
    ...el,
    epochId: BigInt(el.epochId),
    amount: BigInt(el.amount),
    timestamp: new Date(Number(el.timestamp) * 1000),
  }));
}

export async function getRecentClaims(
  fromTimestamp: number,
  chainId: number,
  GQL_URL: string,
  gql: GqlTagType,
  GraphQLClientClass: new (url: string) => GraphQLClientType,
  limit = DEFAULT_LIMIT,
  maxPages = MAX_PAGES,
): Promise<GetRecentClaims> {
  // Convert milliseconds to seconds since database stores timestamps in seconds
  const fromTimestampInSeconds = Math.floor(fromTimestamp / 1000);
  const RECENT_CLAIMS_QUERY = gql`
    query claimsByUser($fromTimestamp: numeric!, $chainId: Int!, $limit: Int!, $after: Int) {
      CedeRewardsSafeModule_RewardsClaimed(
        where: { timestamp: { _gt: $fromTimestamp }, chainId: { _eq: $chainId } }
        limit: $limit
        offset: $after
        order_by: { timestamp: asc }
      ) {
        id
        chainId
        cedeSafe
        epochId
        user
        amount
        timestamp
        user_cedeSafe_epochId
      }
    }
  `;
  type Raw = RecentClaimEvent;
  type Resp = { CedeRewardsSafeModule_RewardsClaimed: Raw[] };
  const allClaims = await gqlHelper<
    Raw,
    { fromTimestamp: number; chainId: number; limit: number; after: number },
    Resp
  >(
    GQL_URL,
    RECENT_CLAIMS_QUERY,
    (offset, limit) => ({ fromTimestamp: fromTimestampInSeconds, chainId, limit, after: offset }),
    (res) => res.CedeRewardsSafeModule_RewardsClaimed,
    GraphQLClientClass,
    limit,
    maxPages,
  );
  return allClaims.map((el) => ({
    ...el,
    epochId: BigInt(el.epochId),
    amount: BigInt(el.amount),
    rewardClaimed: BigInt(el.timestamp),
  }));
}

export async function getCreatedCedeSafe(
  limit = DEFAULT_LIMIT,
  chainId: number,
  GQL_URL: string,
  gql: GqlTagType,
  GraphQLClientClass: new (url: string) => GraphQLClientType,
  maxPages = MAX_PAGES,
): Promise<GetCreatedCedeSafe> {
  // Cede Safe addresses can collide across chains (CREATE2), so scope by chainId.
  const CEDE_SAFE_CREATED_EVENTS = gql`
    query cedeSafeCreatedEvents($chainId: Int!, $limit: Int!, $after: Int) {
      CedeRewardsSafeModule_CedeSafeCreated(
        where: { chainId: { _eq: $chainId } }
        order_by: { timestamp: desc }
        limit: $limit
        offset: $after
      ) {
        cedeSafeAddress
        cedeSafeId
        id
        timestamp
      }
    }
  `;
  type Raw = CedeSafeCreatedEvent;
  type Resp = { CedeRewardsSafeModule_CedeSafeCreated: Raw[] };
  const allResults = await gqlHelper<Raw, { chainId: number; limit: number; after: number }, Resp>(
    GQL_URL,
    CEDE_SAFE_CREATED_EVENTS,
    (offset, limit) => ({ chainId, limit, after: offset }),
    (res) => res.CedeRewardsSafeModule_CedeSafeCreated,
    GraphQLClientClass,
    limit,
    maxPages,
  );
  return allResults.map((el) => ({
    ...el,
    timestamp: new Date(Number(el.timestamp) * 1000),
  }));
}
